import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { t } from '@/i18n';
import type { InputState } from '@/input/types';
import { sfx } from '@/audio/sfx';
import { chamferPanel, label } from './widgets';

/**
 * Gamepad calibration / diagnostics (user request): dumps the RAW Gamepad
 * API every frame — pad id, mapping, every axis and button — next to the
 * game's interpreted input, so platform quirks (Xbox Edge virtual mouse,
 * odd axis layouts) become visible at a glance.
 */
export class CalibrationScreen {
  readonly view = new Container();
  private readonly dim = new Graphics();
  private readonly layer = new Container();
  private readonly body: Text;
  open = false;

  constructor() {
    this.view.visible = false;
    this.body = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Tektur, monospace',
        fontSize: 15,
        fill: 0xd5d9de,
        lineHeight: 22,
      }),
    });
    this.view.addChild(this.dim, this.layer);
  }

  show(screenW: number, screenH: number): void {
    this.open = true;
    this.view.visible = true;
    this.dim.clear().rect(0, 0, screenW, screenH).fill({ color: 0x0a0614, alpha: 0.94 });
    this.layer.removeChildren();

    const panel = new Graphics();
    const w = Math.min(900, screenW - 40);
    const h = screenH - 60;
    chamferPanel(panel, w, h, { fill: 0x110e1b, stroke: COLORS.uiAccent, notch: 16 });
    panel.position.set((screenW - w) / 2, 30);
    this.layer.addChild(panel);

    const title = label(t('calib.title'), 20, COLORS.playerYellow, 3);
    title.position.set((screenW - w) / 2 + 20, 44);
    this.layer.addChild(title);

    const hint = label(t('calib.exit'), 13, 0x6a7076, 2);
    hint.anchor.set(1, 0);
    hint.position.set((screenW + w) / 2 - 20, 48);
    this.layer.addChild(hint);

    this.body.position.set((screenW - w) / 2 + 20, 84);
    this.layer.addChild(this.body);
    this.refresh(null);
  }

  /** Live dump — called every frame while open. */
  refresh(interpreted: InputState | null): void {
    if (!this.open) return;
    const nav = navigator as Navigator & { gamepadInputEmulation?: string };
    const lines: string[] = [];
    lines.push(`UA: ${navigator.userAgent.slice(0, 80)}`);
    lines.push(`gamepadInputEmulation: ${nav.gamepadInputEmulation ?? '(none)'}`);
    lines.push('');

    const pads = navigator.getGamepads?.() ?? [];
    let any = false;
    for (const gp of pads) {
      if (!gp) continue;
      any = true;
      lines.push(`#${gp.index}  ${gp.id.slice(0, 56)}`);
      lines.push(`mapping: "${gp.mapping}"   connected: ${gp.connected}`);
      lines.push(
        'axes: ' + gp.axes.map((a, i) => `[${i}] ${a.toFixed(2)}`).join('  '),
      );
      const pressed = gp.buttons
        .map((b, i) => (b.pressed || b.value > 0.2 ? `[${i}] ${b.value.toFixed(2)}` : null))
        .filter((x): x is string => x !== null);
      lines.push(`buttons: ${pressed.length > 0 ? pressed.join('  ') : '—'}`);
      lines.push('');
    }
    if (!any) {
      lines.push(t('calib.nopad'));
      lines.push('');
    }

    if (interpreted) {
      lines.push('--- ' + t('calib.interpreted') + ' ---');
      lines.push(
        `move: ${interpreted.moveX.toFixed(2)}, ${interpreted.moveY.toFixed(2)}` +
        `   aim: ${interpreted.aimX.toFixed(2)}, ${interpreted.aimY.toFixed(2)}` +
        `   hasAim: ${interpreted.hasAim}`,
      );
      lines.push(
        `fire: ${interpreted.fire}  super: ${interpreted.supershot}` +
        `  dash: ${interpreted.dashPressed}  confirmHeld: ${interpreted.confirmHeld}`,
      );
    }
    this.body.text = lines.join('\n');
  }

  hide(): void {
    this.open = false;
    this.view.visible = false;
  }

  /** Consumes input while open; B/Esc/Start closes. */
  handleInput(input: InputState): boolean {
    if (!this.open) return false;
    this.refresh(input);
    if (input.altPressed || input.pausePressed) {
      sfx.uiMove();
      this.hide();
    }
    return true;
  }
}
